import { useEffect, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import './SiteNavbar.css';
import logo from '../assets/logos/Logo Blue Text PNG  .png';
import logoLight from '../assets/logos/WHITE Logo Text PNG.png';

const navLinks = [
  { path: '/', label: 'Home' },
  // { path: '/about', label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/gallery', label: 'Gallery' },
  { path: '/join-us', label: 'Join Us' },
  { path: '/contact', label: 'Contact' }
];

const SiteNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hideTimeout, setHideTimeout] = useState(null);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const location = useLocation();

  // Auto-hide navbar after inactivity
  const startHideTimer = useCallback(() => {
    if (hideTimeout) clearTimeout(hideTimeout);
    const timer = setTimeout(() => {
      setIsNavbarVisible(false);
    }, 3000); // Hide after 3 seconds of inactivity
    setHideTimeout(timer);
  }, [hideTimeout, setIsNavbarVisible, setHideTimeout]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsNavbarVisible(true);
        startHideTimer();
      } 
      // Hide navbar when scrolling up (only if not at top)
      else if (currentScrollY < lastScrollY && currentScrollY > 50) {
        setIsNavbarVisible(false);
      }
      // Show navbar at top
      else if (currentScrollY < 50) {
        setIsNavbarVisible(false);
      }
      
      setLastScrollY(currentScrollY);
      setIsScrolled(currentScrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [lastScrollY, hideTimeout, startHideTimer]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      navigate('/');
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  // Show navbar on any user action
  const handleMenuToggle = () => {
    setMenuOpen((prev) => !prev);
    setIsNavbarVisible(true);
    startHideTimer();
  };

  return (
    <header className={`site-navbar ${isScrolled ? 'site-navbar--scrolled' : ''} ${!isNavbarVisible ? 'site-navbar--hidden' : ''}`}>
      <div className="site-navbar__inner">
        <button
          type="button"
          className="site-navbar__brand"
          onClick={handleLogoClick}
          aria-label="Go to homepage"
        >
          <img src={isScrolled ? logoLight : logo} alt="The Video Company" />
        </button>

        <nav className={`site-navbar__links ${menuOpen ? 'is-open' : ''}`}>
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `site-navbar__link ${isActive ? 'active' : ''}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="site-navbar__actions">
          <button
            type="button"
            className="ghost-cta"
            onClick={() => handleNavigate('/gallery')}
          >
            Watch Showreel
          </button>
          <button
            type="button"
            className={`nav-toggle ${menuOpen ? 'is-open' : ''}`}
            onClick={handleMenuToggle}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <div className={`site-navbar__mobile ${menuOpen ? 'is-open' : ''}`}>
        {navLinks.map((link) => (
          <NavLink
            key={`mobile-${link.path}`}
            to={link.path}
            className={({ isActive }) =>
              `site-navbar__mobile-link ${isActive ? 'active' : ''}`
            }
          >
            {link.label}
          </NavLink>
        ))}
        {user && (
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `site-navbar__mobile-link ${isActive ? 'active' : ''}`
            }
          >
            Dashboard
          </NavLink>
        )}
        <button
          type="button"
          className="ghost-cta full-width"
          onClick={() =>
            window.open(
              'https://docs.google.com/forms/d/e/1FAIpQLSefqB4bGeoKxFRVLo8i4NfHrkYQjr0Zn0Ru6vKyTfqwjEy8iQ/viewform?usp=header',
              '_blank',
              'noreferrer'
            )
          }
        >
          Get a Quote
        </button>
      </div>
    </header>
  );
};

export default SiteNavbar;
